import { createSelector } from 'reselect';

import { externalLinkOpen } from '../../../../actions/externallink';
import { RedirectServices, getRedirectUrl } from '../../../../config/urls';
import { CustomRouterHistory } from '../../../../createHistory';
import { __ } from '../../../../i18n/l10n';
import { selectEntitiesCategories } from '../../../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import {
  selectEntitiesMissionsEntityOperationType,
  selectEntitiesMissionsEntityOptInEnabled,
  selectEntitiesMissionsEntityRedirectUrl,
  selectEntitiesMissionsEntityRulesUrl,
} from '../../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import { selectEntitiesSports } from '../../../../selectors/entities/sports/entitiesSportsPrimary.selector';
import { StateType } from '../../../../states';
import { Selector } from '../../../../types/selectors';
import { handleInternalNavigation } from '../../../../utils/notification';
import RoutesRedirectionFactory from '../../../../utils/routes/RoutesRedirectionFactory';
import { selectMissionAchievements } from '../../selectors/missionAchievements';
import { selectUserMissionAchievements } from '../../selectors/userMissionAchievements';
import { MissionOperationTypes } from '../../types/state/missions';
import { UserAchievementStatuses } from '../../types/state/userMissions';

const getHistoryFromProps = (_: StateType, { history }: { history?: CustomRouterHistory }) => history;
const getDispatchFromProps = (_: StateType, { dispatch }: { dispatch?: (args: unknown) => void }) => dispatch;

const selectLastUnachievedMissionAchievement = createSelector(
  [selectMissionAchievements, selectUserMissionAchievements],
  (missionAchievements, userMissionAchievements) => {
    const achievedAchievements = userMissionAchievements.filter((uma) => uma?.status === UserAchievementStatuses.WON);

    if (achievedAchievements.length === missionAchievements.length - 1) {
      return missionAchievements.filter(
        (ma) => !userMissionAchievements.find((uma) => uma?.achievementId === ma.achievementId),
      )[0];
    }

    return null;
  },
);

export const selectMissionBetRedirectionCallback: Selector<(() => void) | undefined> = createSelector(
  [
    selectEntitiesMissionsEntityRulesUrl,
    selectEntitiesMissionsEntityOperationType,
    selectEntitiesMissionsEntityOptInEnabled,
    selectEntitiesMissionsEntityRedirectUrl,
    selectLastUnachievedMissionAchievement,
    selectEntitiesSports,
    selectEntitiesCategories,
    getHistoryFromProps,
    getDispatchFromProps,
  ],
  (
    missionRulesURL,
    missionOperationType,
    missionOptInRequired,
    missionRedirectURL,
    lastUnachievedAchievement,
    sports,
    categories,
    history,
    dispatch,
  ) => {
    if (history && dispatch) {
      if (missionOperationType === MissionOperationTypes.OTHER && !missionOptInRequired) {
        return () => {
          if (missionRulesURL) {
            dispatch(
              externalLinkOpen(getRedirectUrl(RedirectServices.ENCODED_URL, { url: missionRulesURL }), { auth: true }),
            );
          } else if (missionRedirectURL) {
            handleInternalNavigation({
              history,
              dispatch,
              uri: missionRedirectURL,
            });
          }
        };
      }

      if (lastUnachievedAchievement?.eligibility) {
        const { allowedSportIds, allowedCategoryIds, allowedTournamentIds, allowedMatchIds } =
          lastUnachievedAchievement.eligibility;

        if (
          !allowedSportIds?.length &&
          !allowedCategoryIds?.length &&
          !allowedTournamentIds?.length &&
          !allowedMatchIds?.length &&
          !missionRedirectURL
        ) {
          return () => {
            history.push(RoutesRedirectionFactory.getHomeRouteConfig());
          };
        }

        const matchIdToRedirect = allowedMatchIds?.[0];

        if (matchIdToRedirect) {
          return () => {
            handleInternalNavigation({ history, dispatch, uri: `wam://betting?target=match-${matchIdToRedirect}` });
          };
        }

        const tournamentIdToRedirect = allowedTournamentIds?.[0];

        if (tournamentIdToRedirect) {
          const categoryIds = Object.keys(categories ?? {}).map((categoryIdAsKey) => parseInt(categoryIdAsKey, 10));
          const categoryId = categoryIds.find(
            (categoryId) => (categories[categoryId]?.tournaments?.indexOf(tournamentIdToRedirect) ?? -1) !== -1,
          );

          if (categoryId) {
            const sportIds = Object.keys(sports ?? {}).map((sportIdAsKey) => parseInt(sportIdAsKey, 10));
            const sportId = sportIds.find((sportId) => (sports[sportId]?.categories?.indexOf(categoryId) ?? -1) !== -1);

            if (sportId) {
              return () => {
                handleInternalNavigation({
                  history,
                  dispatch,
                  uri: `wam://betting?target=catalog-${sportId}-${categoryId}-${tournamentIdToRedirect}`,
                });
              };
            }
          }
        }

        const categoryIdToRedirect = allowedCategoryIds?.[0];

        if (categoryIdToRedirect) {
          const sportIds = Object.keys(sports ?? {}).map((sportIdAsKey) => parseInt(sportIdAsKey, 10));
          const sportId = sportIds.find(
            (sportId) => (sports[sportId]?.categories?.indexOf(categoryIdToRedirect) ?? -1) !== -1,
          );

          if (sportId) {
            return () => {
              handleInternalNavigation({
                history,
                dispatch,
                uri: `wam://betting?target=catalog-${sportId}-${categoryIdToRedirect}`,
              });
            };
          }
        }

        const sportIdToRedirect = allowedSportIds?.[0];

        if (sportIdToRedirect) {
          return () => {
            handleInternalNavigation({ history, dispatch, uri: `wam://betting?target=catalog-${sportIdToRedirect}` });
          };
        }
      }

      if (missionRedirectURL) {
        return () => {
          handleInternalNavigation({ history, dispatch, uri: missionRedirectURL });
        };
      }

      return () => {
        history.push(RoutesRedirectionFactory.getHomeRouteConfig());
      };
    }
  },
);

const selectMissionDetailsBetRedirectionButtonContainerProps = createSelector(
  [selectMissionBetRedirectionCallback],
  (callback) => ({
    label: __('!action:Bet'),
    onClick: callback,
  }),
);

export default selectMissionDetailsBetRedirectionButtonContainerProps;
